import { resetHasFetched, shouldFetch } from "shared/utils";
import { defineStore } from "pinia";
import type { ActionplanBatch, ActionplanTabCounts } from "actionplan/types";
import type { PaginatedResult, PaginationState } from "api/types";
import { extractPaginationState } from "api/mappers";
import { selectedFiltersToQueryParams } from "shared/mappers/filter.mapper";
import type { Filter, HasFetchedState, SelectedFilters } from "shared/types";
import { ref } from "vue";

/**
 * Fetchers backing an actionplans list store. `context` is the class code for
 * the classroom tabs (mentor/lesson class) and `null` for the school-wide
 * overview, which ignores it.
 */
export type ActionplanListFetchers = {
  fetchList: (
    params: Record<string, string>,
    context: string | null,
  ) => Promise<PaginatedResult<ActionplanBatch>>;
  fetchCounts: (
    params: Record<string, string>,
    context: string | null,
  ) => Promise<ActionplanTabCounts>;
  fetchFilters: (context: string | null) => Promise<Filter[]>;
};

/**
 * Guard for classroom fetchers: the class code must be set before fetching.
 * Fails loudly instead of building a malformed URL (e.g. `my-lessongroups//...`).
 */
export const requireActionplanContext = (context: string | null): string => {
  if (context === null || context === "") {
    throw new Error(
      "Actionplan class store used without a class context; call setContext first.",
    );
  }

  return context;
};

type FetchingMap = {
  actionplans: false | Promise<PaginatedResult<ActionplanBatch>>;
  actionplanFilters: false | Promise<Filter[]>;
  actionplanCounts: false | Promise<ActionplanTabCounts>;
};

/**
 * Builds a Pinia store that owns fetching + pagination for an actionplans list.
 * A list row is one request rather than one plan
 * (ACTIONPLAN-TEACHER-LISTS-ONE-ROW-PER-BATCH-001).
 * The school-wide overview and both classroom class tabs share this body; they
 * only differ in the endpoints their fetchers hit (via `fetchers` + `context`).
 */
export const createActionplanListStore = (
  storeId: string,
  fetchers: ActionplanListFetchers,
) =>
  defineStore(storeId, () => {
    const actionplans = ref<ActionplanBatch[]>([]);
    const actionplansPagination = ref<PaginationState | null>(null);
    const actionplanFilters = ref<Filter[]>([]);
    const counts = ref<ActionplanTabCounts>({ open: 0, completed: 0 });

    // Class code for the classroom tabs; null for the overview.
    const context = ref<string | null>(null);

    const isFetching = ref<FetchingMap>({
      actionplans: false,
      actionplanFilters: false,
      actionplanCounts: false,
    });

    const hasFetched = ref<HasFetchedState>({
      actionplans: false,
      actionplanFilters: false,
      actionplanCounts: false,
    });

    // Track the params of the in-flight request so the dedup only reuses a
    // pending promise when the filters match, and so a stale response (from a
    // superseded filter set) can't overwrite fresher state.
    let actionplansKey: string | null = null;
    let actionplanCountsKey: string | null = null;
    let actionplanFiltersKey: string | null = null;

    const $reset = () => {
      actionplans.value = [];
      actionplansPagination.value = null;
      actionplanFilters.value = [];
      counts.value = { open: 0, completed: 0 };
      actionplansKey = null;
      actionplanCountsKey = null;
      actionplanFiltersKey = null;
      resetHasFetched(hasFetched.value);
    };

    // Point the store at a different class. Clears cached data so the next
    // fetch reloads for the new context.
    const setContext = (next: string | null) => {
      if (next === context.value) {
        return;
      }
      context.value = next;
      $reset();
    };

    const loadActionplans = async (
      filters: SelectedFilters = {},
    ): Promise<PaginatedResult<ActionplanBatch>> => {
      const params = selectedFiltersToQueryParams(filters);
      const key = JSON.stringify([context.value, params]);

      if (isFetching.value.actionplans !== false && actionplansKey === key) {
        return isFetching.value.actionplans;
      }

      actionplansKey = key;

      isFetching.value.actionplans = (async () => {
        try {
          const result = await fetchers.fetchList(params, context.value);

          // Ignore a stale response when newer filters have been requested.
          if (actionplansKey === key) {
            actionplans.value = result.items;
            actionplansPagination.value = extractPaginationState(result);
            hasFetched.value.actionplans = new Date();
          }

          return result;
        } finally {
          if (actionplansKey === key) {
            isFetching.value.actionplans = false;
          }
        }
      })();

      return isFetching.value.actionplans;
    };

    const loadActionplanFilters = async ({
      forceFetch = false,
    }: {
      forceFetch?: boolean;
    } = {}): Promise<Filter[]> => {
      // Filters depend only on the context (class), so key the request by it: a
      // response for a superseded class must not overwrite state or set
      // hasFetched, which would block a fresh fetch for the new class.
      const key = JSON.stringify(context.value);

      if (
        isFetching.value.actionplanFilters !== false &&
        actionplanFiltersKey === key
      ) {
        return isFetching.value.actionplanFilters;
      }

      if (
        forceFetch === false &&
        shouldFetch(hasFetched.value.actionplanFilters) === false
      ) {
        return actionplanFilters.value;
      }

      actionplanFiltersKey = key;

      isFetching.value.actionplanFilters = (async () => {
        try {
          const result = await fetchers.fetchFilters(context.value);

          // Ignore a stale response when the context has since changed.
          if (actionplanFiltersKey === key) {
            actionplanFilters.value = result;
            hasFetched.value.actionplanFilters = new Date();
          }

          return result;
        } finally {
          if (actionplanFiltersKey === key) {
            isFetching.value.actionplanFilters = false;
          }
        }
      })();

      return isFetching.value.actionplanFilters;
    };

    const loadActionplanCounts = async (
      filters: SelectedFilters = {},
    ): Promise<ActionplanTabCounts> => {
      const params = selectedFiltersToQueryParams(filters);
      const key = JSON.stringify([context.value, params]);

      if (
        isFetching.value.actionplanCounts !== false &&
        actionplanCountsKey === key
      ) {
        return isFetching.value.actionplanCounts;
      }

      actionplanCountsKey = key;

      isFetching.value.actionplanCounts = (async () => {
        try {
          const result = await fetchers.fetchCounts(params, context.value);

          // Ignore a stale response when newer filters have been requested.
          if (actionplanCountsKey === key) {
            counts.value = result;
            hasFetched.value.actionplanCounts = new Date();
          }

          return result;
        } finally {
          if (actionplanCountsKey === key) {
            isFetching.value.actionplanCounts = false;
          }
        }
      })();

      return isFetching.value.actionplanCounts;
    };

    return {
      actionplans,
      actionplansPagination,
      actionplanFilters,
      counts,
      isFetching,
      hasFetched,
      setContext,
      loadActionplans,
      loadActionplanFilters,
      loadActionplanCounts,
      $reset,
    };
  });
